import React, {useCallback, useEffect, useMemo, useState} from 'react';
import TextField from "@mui/material/TextField";
import MenuItem from '@mui/material/MenuItem';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';

// This fixes the DDLB transparency issue
const theme = createTheme({
    backgroundColor: "white"
});


export default function ArgoTextFieldSelectHook(props) {
    const fileName = props.fileName ?? "NoFileName";
    const fieldName = props.fieldName ?? "NoFieldName";
    const index = props.index ?? ""; // If field is built in a loop index is required to know which field to match up with
    const uniqueIdentifier = `${fileName}-ArgoTextFieldSelectHook-${fieldName}-${index}`;

    const variant = props.variant ?? "standard";
    const label = props.label ?? "";
    const value = props.value ?? "";
    const width = props.width ?? "";
    const paddingBottom = props.paddingBottom ?? "0px";
    const paddingTop = props.paddingTop ?? "0px";
    const errorText = props.errorText ?? " ";
    const icon = props.icon ?? undefined

    const fullWidth = props.fullWidth !== undefined ? props.fullWidth : (width === "");
    const onChange = props.onChange ?? (() => {});
    const disabled = props.disabled ?? false;
    const addAll = props.addAll === true;

    const stateObjName = props.stateObjName ?? "";
    const metadataTag = props.metadataTag ?? "";

    const valueProperty = props.valueProperty ?? "value";
    const textProperty = props.textProperty ?? "text";
    const useTextForValue = props.useTextForValue === true;

    const dropDownHeight = props.dropDownHeight ?? null
    const dropDownWidth = props.dropDownWidth ?? null

    const rawMenuItems = useMemo(() => {
        if (!Array.isArray(props.menuItems)) return []
        return props.menuItems.filter(Boolean);
    }, [props.menuItems])

    const MenuProps = {
        MenuProps: {
            PaperProps: {
                style: {
                    maxHeight: dropDownHeight,
                    width: dropDownWidth,
                }
            }
        }
    }

    const menuItemsIsStrings = rawMenuItems.length === 0 ? true : typeof rawMenuItems[0] === "string";

    const menuItems = useMemo(() => {
        if (!addAll) {
            return rawMenuItems;
        }

        if (menuItemsIsStrings) {
            return ["All", ...rawMenuItems];
        }

        const augmentedItems = [{[valueProperty]: "All", [textProperty]: "All"}, ...rawMenuItems];
        return augmentedItems.filter(item => item[valueProperty] !== undefined && item[textProperty] !== undefined);
    }, [addAll, rawMenuItems, menuItemsIsStrings, valueProperty, textProperty]);

    const [error, setError] = useState(() => errorText.trim() !== "");

    useEffect(() => {
        setError(errorText.trim() !== "");
    }, [errorText]);

    const resolveMenuItemText = useCallback((selectedValue) => {
        if (menuItemsIsStrings) {
            if (useTextForValue) {
                return String(selectedValue);
            }

            if (addAll && selectedValue === "All") {
                return "All";
            }

            const numericIndex = Number(selectedValue);
            if (!Number.isNaN(numericIndex) && menuItems[numericIndex] !== undefined) {
                return menuItems[numericIndex];
            }

            return String(selectedValue);
        }

        const comparisonKey = useTextForValue ? textProperty : valueProperty;
        const foundItem = menuItems.find(item => item && item[comparisonKey] === selectedValue);
        return foundItem ? foundItem[textProperty] : "";
    }, [addAll, menuItems, menuItemsIsStrings, textProperty, useTextForValue, valueProperty]);

    const handleChange = (event) => {
        const selectedValue = event.target.value;
        const menuItemText = resolveMenuItemText(selectedValue);
        onChange(event, fieldName, menuItemText, index, stateObjName, metadataTag);
    };

    const options = useMemo(() => menuItems.reduce((acc, item, idx) => {
        if (menuItemsIsStrings) {
            const optionLabel = item;
            if (optionLabel === undefined) {
                return acc;
            }

            let optionValue;

            if (useTextForValue) {
                optionValue = item;
            } else if (addAll) {
                optionValue = idx === 0 ? "All" : idx;
            } else {
                optionValue = idx;
            }

            acc.push({value: optionValue, label: optionLabel});
            return acc;
        }

        if (item[textProperty] === undefined || item[valueProperty] === undefined) {
            return acc;
        }

        const optionLabel = item[textProperty];
        const optionValue = useTextForValue ? item[textProperty] : item[valueProperty];
        acc.push({value: optionValue, label: optionLabel});
        return acc;
    }, []), [menuItems, menuItemsIsStrings, useTextForValue, addAll, textProperty, valueProperty]);

    if (options.length === 0) {
        return <div></div>;
    }

    return (
        <div style={{width: fullWidth ? "100%" : width}}>
            <ThemeProvider theme={theme}>
                <TextField
                    id={uniqueIdentifier}
                    name={uniqueIdentifier}
                    select
                    variant={variant}
                    label={
                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                            <span>{label}</span>
                            {icon}
                        </div>
                    }
                    value={value}
                    disabled={disabled}
                    onChange={handleChange}
                    fullWidth={fullWidth}
                    error={error}
                    helperText={errorText}
                    sx={{width: fullWidth ? "100%" : width, paddingBottom, paddingTop}}
                    SelectProps={MenuProps}
                >
                    {options.map((option, idx) => (
                        <MenuItem key={`${uniqueIdentifier}-${idx}`} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </ThemeProvider>
        </div>
    );
}


// Loop through objects with map
// {Object.keys(this.props.types).map(type =>
//     <MenuItem key={type} value={type} primaryText={self.props.types[type]}/>
// )}
